import axios from "axios";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { getMenuState, type LunchSold, type MenuResponse } from "../api/menu";
import { getEdgeBaseUrl } from "../lib/edge/edgeState";

/**
 * Малката проверка на менюто за `useLunchMenuCheck` (клик на маса, „Добави",
 * след обедна поръчка): вместо цялото GET /menu/ (1 MB+) — GET /menu-state с
 * версията на ЦЕЛИЯ отговор и броячите на обедните порции.
 *
 * - "changed" — версията е друга → викащият тегли цялото меню, както преди;
 * - "counters" — различни са само продадените порции → кръпка на кеша
 *   (`items` и всичко друго остават същите референции);
 * - "unchanged" — каталогът остава, мести се само `dataUpdatedAt` (без
 *   прерисуване, без ново теглене от абонат, монтиран скоро след това);
 * - "refreshed" — докато чакахме, кешът получи пълно меню със същата версия,
 *   каквато съобщи проверката (с друга версия → "changed"); броячите не са във
 *   версията — печелят тези от по-новия отговор по часовника на сървъра.
 *
 * При "changed" се връща и версията, която проверката видя: пълното теглене
 * може да се присъедини към заявка, тръгнала преди промяната (`useLunchMenuCheck`).
 *
 * Само без локален резерв: с резерв проверката тегли цялото меню, за да
 * остане огледалото на касата свежо (docs/edge.md). Грешка се хвърля към
 * викащия — той я гълта като досега (кешът остава, без второ теглене).
 * Изключение: облакът отговаря, но маршрута го няма (върнат бекенд, стар pod
 * при плавен деплой) → "changed", т.е. цялото меню както преди.
 */
export type MenuStateOutcome = "changed" | "counters" | "unchanged" | "refreshed";

export interface MenuStateCheck {
  outcome: MenuStateOutcome;
  /** Версията, която видя проверката (при "changed" от отговора). */
  version?: string;
}

// Отделен ключ, НЕ под ["menu"]: инвалидациите и `isFetching` на менюто не го броят
const MENU_STATE_QUERY_KEY = ["menu-state"];

/** Може ли проверката да мине с GET /menu-state (иначе — цялото меню, както преди). */
export const canCheckMenuState = (queryClient: QueryClient, menuKey: QueryKey) =>
  Boolean(queryClient.getQueryData<MenuResponse>(menuKey)?.menuVersion) &&
  getEdgeBaseUrl() == null &&
  queryClient.isFetching({ queryKey: menuKey }) === 0;

/**
 * 404 на САМИЯ маршрут (FastAPI: `detail` "Not Found") или 405 — бекендът е
 * без /menu-state. „Непознат ресторант" (404 с друг `detail`) и мрежовите
 * грешки НЕ са това: там и цялото меню би паднало.
 */
export const isMenuStateRouteMissing = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  return status === 405 || (status === 404 && detail === "Not Found");
};

// Числото `retry` от настройките на приложението (main.tsx: 1), за да не се разминава
const defaultRetryCount = (queryClient: QueryClient) => {
  const retry = queryClient.getDefaultOptions().queries?.retry;
  return typeof retry === "number" ? retry : 0;
};

const lunchSoldKey = (lunchMenuId: number, itemId: number) => `${lunchMenuId}:${itemId}`;

// По-нов ли е отговорът с `later` (часовникът на сървъра при отговора). Меню без
// часовник (без активни менюта) няма и броячи — всеки отговор с часовник е по-нов.
const isServerTimeLater = (later: string | null, earlier: string | null | undefined) =>
  later != null && (earlier == null || Date.parse(later) > Date.parse(earlier));

/**
 * Менюто с броячите от /menu-state — същото `soldToday`, което би донесло
 * цялото GET /menu/ (0 без продажби, само за продуктите с дневен лимит).
 * Нищо различно → същата референция.
 */
export const withLunchSold = (
  menu: MenuResponse,
  lunchSold: LunchSold[],
  serverTime: string | null,
): MenuResponse => {
  const soldByKey = new Map(
    lunchSold.map((row) => [lunchSoldKey(row.lunchMenuId, row.itemId), row.soldToday]),
  );
  let changed = false;
  const lunchMenus = (menu.lunchMenus ?? []).map((lunchMenu) => {
    let menuChanged = false;
    const items = lunchMenu.items.map((lunchItem) => {
      if ((lunchItem.dailyLimit ?? 0) <= 0) return lunchItem;
      const soldToday = soldByKey.get(lunchSoldKey(lunchMenu.id, lunchItem.itemId)) ?? 0;
      if ((lunchItem.soldToday ?? 0) === soldToday) return lunchItem;
      menuChanged = true;
      return { ...lunchItem, soldToday };
    });
    if (!menuChanged) return lunchMenu;
    changed = true;
    return { ...lunchMenu, items };
  });
  // `serverTime` — към него са броячите, както в пълното меню
  return changed ? { ...menu, lunchMenus, serverTime } : menu;
};

// Часът на сървъра, към който /menu-state е ПОТВЪРДИЛ броячите на това меню,
// когато стойностите са същите: менюто в кеша нарочно не се сменя (без
// прерисуване), затова часът се пази тук, по самия обект на менюто.
const countersConfirmedAt = new WeakMap<MenuResponse, string>();

/** Към кой час на сървъра са броячите на менюто (собственият или потвърденият). */
const countersTimeOf = (menu: MenuResponse): string | null => {
  const confirmed = countersConfirmedAt.get(menu) ?? null;
  const own = menu.serverTime ?? null;
  return isServerTimeLater(confirmed, own) ? confirmed : own;
};

const confirmCounters = (menu: MenuResponse, serverTime: string | null) => {
  if (serverTime != null && isServerTimeLater(serverTime, countersTimeOf(menu)))
    countersConfirmedAt.set(menu, serverTime);
};

// Броячите на менюто във вида на /menu-state (само продуктите с дневен лимит)
const lunchSoldOf = (menu: MenuResponse): LunchSold[] =>
  (menu.lunchMenus ?? []).flatMap((lunchMenu) =>
    lunchMenu.items
      .filter((lunchItem) => (lunchItem.dailyLimit ?? 0) > 0)
      .map((lunchItem) => ({
        lunchMenuId: lunchMenu.id,
        itemId: lunchItem.itemId,
        soldToday: lunchItem.soldToday ?? 0,
      })),
  );

/**
 * Пълно меню, изчислено ПРЕДИ броячите в кеша (кърпени от по-нова проверка —
 * пълното теглене е тръгнало по време на нея и е пристигнало последно), не
 * връща порциите назад: същата версия и по-стар часовник → броячите от кеша.
 * Вика се от `queryFn` на менюто — единственият, който пише пълно меню в кеша.
 */
export const keepNewerLunchCounters = (
  fetched: MenuResponse,
  cached: MenuResponse | undefined,
): MenuResponse => {
  if (!cached?.menuVersion || cached.menuVersion !== fetched.menuVersion) return fetched;
  const cachedTime = countersTimeOf(cached);
  if (!isServerTimeLater(cachedTime, fetched.serverTime)) return fetched;
  const kept = withLunchSold(fetched, lunchSoldOf(cached), cachedTime);
  // Същите стойности → новият обект пази потвърждението на кеша
  if (kept === fetched) confirmCounters(fetched, cachedTime);
  return kept;
};

export const checkMenuState = async (
  queryClient: QueryClient,
  menuKey: QueryKey,
  restaurantName: string,
): Promise<MenuStateCheck> => {
  const cached = queryClient.getQueryData<MenuResponse>(menuKey);
  const updatedAtBefore = queryClient.getQueryState(menuKey)?.dataUpdatedAt;
  if (!cached?.menuVersion) return { outcome: "changed" };
  const knownVersion = cached.menuVersion;
  // През react-query: едновременните проверки (StrictMode, „Поръчай" + клик)
  // делят една заявка, а повторният опит и глобалните грешки са като на менюто
  let state;
  try {
    state = await queryClient.fetchQuery({
      queryKey: MENU_STATE_QUERY_KEY,
      queryFn: () => getMenuState(restaurantName),
      staleTime: 0,
      gcTime: 0,
      // Липсващият маршрут не се повтаря; иначе — повторенията на приложението
      retry: (failureCount, error) =>
        !isMenuStateRouteMissing(error) && failureCount < defaultRetryCount(queryClient),
    });
  } catch (error) {
    // Без маршрута — цялото меню, както преди; стар бекенд го връща без
    // версия и следващите проверки сами минават по стария път
    if (isMenuStateRouteMissing(error)) return { outcome: "changed" };
    throw error;
  }
  const changed: MenuStateCheck = { outcome: "changed", version: state.version };
  if (queryClient.getQueryState(menuKey)?.dataUpdatedAt !== updatedAtBefore) {
    // Пълно меню дойде по друг път (напр. фокус), докато чакахме: може да е
    // изчислено ПРЕДИ промяната, която проверката вече вижда — сверяваме версиите
    const arrived = queryClient.getQueryData<MenuResponse>(menuKey);
    if (!arrived || arrived.menuVersion !== state.version) return changed;
    // Броячите не са във версията: по-новият от двата отговора печели
    if (!isServerTimeLater(state.serverTime, countersTimeOf(arrived))) return { outcome: "refreshed" };
    const patchedArrived = withLunchSold(arrived, state.lunchSold, state.serverTime);
    if (patchedArrived === arrived) {
      confirmCounters(arrived, state.serverTime);
      return { outcome: "refreshed" };
    }
    queryClient.setQueryData(menuKey, patchedArrived);
    return { outcome: "counters" };
  }
  if (state.version !== knownVersion) return changed;
  const patched = withLunchSold(cached, state.lunchSold, state.serverTime);
  // Същите броячи: менюто остава същото, но часът на потвърждението се помни —
  // по-старо пълно меню, пристигнало след проверката, не бива да ги върне назад
  if (patched === cached) confirmCounters(cached, state.serverTime);
  // Същата референция → без прерисуване; `updatedAt` — менюто е проверено сега
  queryClient.setQueryData(menuKey, patched, { updatedAt: Date.now() });
  return { outcome: patched === cached ? "unchanged" : "counters" };
};
